import {
  useBrowseProductEntitlementApi,
  useBrowseSubscriptionPlanApi,
} from '@aibees/shared/api';
import { CatalogProduct, Plan, PlanInterval } from '@aibees/shared/definition';
import { useMemo } from 'react';
import { isFreePlanSet, planFamilyKey, productFamilyKey } from '../catalog';

export interface PlanCardEntry {
  product: CatalogProduct;
  plans: Plan[];
  isFree: boolean;
}

function sortProducts(products: CatalogProduct[]): CatalogProduct[] {
  return [...products].sort(
    (a, b) =>
      (Number(a.metadata?.order) || 0) - (Number(b.metadata?.order) || 0)
  );
}

function getProductPlans(
  product: CatalogProduct,
  plans: Plan[] | undefined,
  freePlan: Plan | undefined
): Plan[] {
  const key = productFamilyKey(product);
  if (!key) return [];
  const paid = plans?.filter((p) => planFamilyKey(p) === key) ?? [];
  const free = freePlan && planFamilyKey(freePlan) === key ? [freePlan] : [];
  return [...free, ...paid.filter((p) => !!p.amount)];
}

export function usePlanList(yearly: boolean) {
  const interval = yearly ? PlanInterval.YEAR : PlanInterval.MONTH;
  const { data: plans, isLoading: plansLoading } =
    useBrowseSubscriptionPlanApi(interval);
  const { data: monthlyPlans } = useBrowseSubscriptionPlanApi(
    PlanInterval.MONTH
  );
  const { data: entitlementData, isLoading: productsLoading } =
    useBrowseProductEntitlementApi();

  const freePlan = monthlyPlans?.find((plan) => plan.amount === 0);

  // No refetch-on-`yearly` effect: `interval` is part of
  // useBrowseSubscriptionPlanApi's query key, so React Query refetches on its
  // own when the toggle flips.

  const productCards = useMemo<PlanCardEntry[]>(() => {
    const products = sortProducts(
      (entitlementData?.products ?? []).filter(
        (p) => p.active && p.metadata?.is_public === 'true'
      )
    );

    return products.map((product) => {
      const productPlans = getProductPlans(product, plans, freePlan);
      return {
        product,
        plans: productPlans,
        isFree: isFreePlanSet(productPlans),
      };
    });
  }, [entitlementData, plans, freePlan]);

  return {
    plans,
    freePlan,
    productCards,
    isLoading: plansLoading || productsLoading,
  };
}
